import React, { useState, useMemo } from 'react';
import { Search, Upload } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import type { MedaImage } from '../types/image';

interface ImagePickerProps {
  selectedId: string | null;
  onSelect(image: MedaImage): void;
  onUpload(file: File): void;
}

export default function ImagePicker({ selectedId, onSelect, onUpload }: ImagePickerProps) {
  const { images } = useLibrary();
  const [search, setSearch] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search) return images;
    const q = search.toLowerCase();
    return images.filter(i => i.name.toLowerCase().includes(q));
  }, [images, search]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg flex-1"
          style={{ backgroundColor: 'var(--sage-bg)', border: '1px solid var(--sage-white-10)' }}
        >
          <Search size={13} style={{ color: 'var(--sage-text-muted)' }} />
          <input
            type="text"
            placeholder="Search library..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-xs flex-1"
            style={{ color: 'var(--sage-text)' }}
          />
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs"
          style={{
            backgroundColor: 'var(--sage-bg)',
            color: 'var(--sage-text-muted)',
            border: '1px solid var(--sage-white-10)',
          }}
        >
          <Upload size={13} />
          Upload
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
          }}
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-center py-6" style={{ color: 'var(--sage-text-muted)' }}>
          {images.length === 0 ? 'No images in library. Upload one above.' : 'No matches.'}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto sage-scrollbar-hide">
          {filtered.map(img => (
            <button
              key={img.id}
              onClick={() => onSelect(img)}
              className="aspect-square rounded-lg overflow-hidden transition-all"
              style={{
                border: selectedId === img.id
                  ? '2px solid var(--sage-primary)'
                  : '2px solid transparent',
                opacity: selectedId === img.id ? 1 : 0.7,
              }}
            >
              <img
                src={URL.createObjectURL(img.thumbnail)}
                alt={img.name}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
